<template>
  <!-- A static row scored on a bid the campaign no longer has, i.e. between an inline bid edit
       and the refetch that follows it. Anything below would be computed from the old bid -->
  <div
    v-if="isPositionStale"
    v-b-tooltip.hover
    class="text-muted"
    :title="stalePositionTooltip"
    data-cy="text-position-stale"
  >
    -
  </div>

  <!-- Slice H, behind rx-market-intelligence (§3.9): a Static campaign's suggestion as a chip,
       "+$20.05" for a raise that keeps its minimum and "$50.05 · 90 s" for a switch of bid and
       minimum. No calls/day estimate on either (R15; I9; R42). Its hover says what it does and the
       row's Missed count (R54); the confirmation repeats the count -->
  <b-button
    v-else-if="showsSuggestionChip"
    v-b-tooltip.hover
    variant="outline-primary"
    size="sm"
    class="text-nowrap"
    :title="suggestionTitle"
    :data-cy="suggestedSetup.changesMinimum ? 'button-apply-suggested-setup' : 'button-apply-suggested-bid'"
    @click="$emit('apply', suggestionPayload)"
  >
    {{ suggestionChipText }}
  </b-button>

  <!-- A RingbaRTB or Programmatic campaign's suggestion, as advice. Its bidder or its API sets the
       terms, so there is nothing to apply from here. The advice is its own sentence, so its hover is
       the row's Missed count alone, and none at 0 (R54); on a row E1 flagged as losing on conversion
       it opens with the note on why this setup (R62) -->
  <div
    v-else-if="showsAdvice"
    v-b-tooltip.hover
    class="opportunity-advice"
    :title="adviceTitle"
    data-cy="text-suggested-setup-advice"
  >
    {{ adviceText }}
  </div>

  <!-- R59, R62: a campaign that loses on conversion rather than on price and that E1 has no setup to
       offer is told to better its conversion rate, and nothing about its bid. The hover says its
       conversion rate counts against its bid, then the two levers it holds. No Missed sentence: there
       is no suggestion here for the count to sit beside, and the column next to it shows it -->
  <div
    v-else-if="showsConversionAdvice"
    v-b-tooltip.hover
    class="opportunity-advice"
    :title="conversionAdviceTooltip"
    data-cy="text-conversion-advice"
  >
    {{ conversionAdviceText }}
  </div>

  <!-- Today's chip, outside the rollout or on a row from before Slice H -->
  <b-button
    v-else-if="hasSuggestion"
    variant="outline-primary"
    size="sm"
    class="text-nowrap"
    data-cy="button-apply-suggested-bid"
    @click="$emit('apply', {
      campaign,
      suggestedBidInUSD: marketPosition.suggestedBidInUSD,
      estimatedAdditionalCallsPerDay: marketPosition.estimatedAdditionalCallsPerDay
    })"
  >
    +{{ formatCurrency(raiseAmountInUSD) }}
    <span v-if="marketPosition.estimatedAdditionalCallsPerDay">
      → est. +{{ marketPosition.estimatedAdditionalCallsPerDay }} calls/day
    </span>
    <span v-else>
      → enters market range
    </span>
  </b-button>

  <!-- Already at or above the suggestion - the state a campaign lands in right after acting
       on a raise, which would otherwise read as a blank cell -->
  <div
    v-else-if="isAtSuggestedBid"
    v-b-tooltip.hover
    class="text-muted text-nowrap"
    :title="atSuggestedBidTooltip"
    data-cy="text-at-suggested-bid"
  >
    At suggested bid
  </div>

  <!-- R60: a positioned bid with no badge - too few winning bids from other buyers to position it.
       A dash with no hover, as the Market Position column's on the same row: buyer copy never says a
       market lacks activity or evidence, nor that a bid has no position (R64) -->
  <div
    v-else-if="isUnpositioned"
    class="text-muted"
    data-cy="text-opportunity-unpositioned"
  >
    -
  </div>

  <!-- R60: a positioned bid with a badge, nothing above to show, and a market with no suggested
       setup: "No suggested bid yet", with no hover (R64). Flag on or off (I8, I40) -->
  <div
    v-else-if="showsNoSuggestionReason"
    class="opportunity-reason text-muted"
    data-cy="text-no-suggestion"
  >
    {{ noSuggestionText }}
  </div>

  <!-- A dynamic campaign that placed no bids in the window, no row for the campaign, or any other
       positioned bid with no suggestion (R66, 2026-11-02: it read "Outbid, no raise suggested", "No
       change suggested" or "No raise suggested", each with a hover) -->
  <div
    v-else
    class="text-muted"
    data-cy="text-opportunity-none"
  >
    -
  </div>
</template>

<script>
import formatCurrency from '@/utils/formatCurrency'
import {
  CONVERSION_ADVICE_SENTENCE,
  formatMarketWindow,
  getConversionAdviceText,
  getConversionAdviceTooltip,
  getMinimumText,
  getMissedAuctionsText,
  getNoSuggestionText,
  getSuggestionActionText,
  getOpportunity,
  MARKET_CONTEXT_WINDOW_HOURS,
  opportunityKinds
} from '@/store/modules/market-context.store'

export default {
  name: 'BidOpportunityCell',

  props: {
    campaign: {
      type: Object,
      required: true
    },

    // Market position row for this campaign, see MarketContextAPI.getCampaignMarketPositions
    marketPosition: {
      type: Object,
      default: null
    },

    // Static campaigns are the only ones that can act on a raise suggestion:
    // campaign/updateCampaignBid writes maxBidPerCallInUSD, which an RTB or Programmatic
    // campaign ignores because its bidder prices every ping itself
    isStatic: {
      type: Boolean,
      default: true
    },

    // The window the list's rows were measured over, as the `short` form of formatMarketWindow
    // ('24h' or '7d'), for the at-suggested-bid hover on a row from before backend PR 1, which
    // carries no window of its own. Empty until the positions load
    windowShort: {
      type: String,
      default: ''
    },

    // The same window as the `long` form of formatMarketWindow ('last 24 hours' or 'last 7 days'),
    // for the row's Missed count on a hover, which is the Missed column's and in its window (R54):
    // beside a suggestion, and in the reasons a row has none (R60). Empty until the positions load,
    // and the sentence is left out then
    windowLong: {
      type: String,
      default: ''
    },

    // Inside the rx-market-intelligence rollout (the list's showMarketIntelligence): the cell shows
    // Slice H's suggestions on a row that carries them, and today's chip otherwise (§4.6)
    marketIntelligence: {
      type: Boolean,
      default: false
    },

    // The campaign's routing type and settings word a dynamic row's advice: a RingbaRTB bidder
    // returns its terms, a Programmatic campaign fixes or parses each of its own (I16)
    routingType: {
      type: String,
      default: ''
    },

    programmaticBidSettings: {
      type: Object,
      default: null
    },

    // A CPA-only category has no minimum: its suggestions only raise the bid (I5), and a
    // Programmatic campaign there is told its static bid alone (I42)
    isCpaOnly: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Which of the template's states the row is in, tried in the template's order, and what that
    // state reads. The list counts its rows by the same function for the usage events
    // (docs/MARKET_CONTEXT_USAGE_ANALYTICS.md)
    opportunity() {
      return getOpportunity({
        campaign: this.campaign,
        marketPosition: this.marketPosition,
        isStatic: this.isStatic,
        marketIntelligence: this.marketIntelligence,
        routingType: this.routingType,
        isCpaOnly: this.isCpaOnly,
        programmaticBidSettings: this.programmaticBidSettings
      })
    },

    kind() {
      return this.opportunity.kind
    },

    showsSuggestionChip() {
      return this.kind === opportunityKinds.RaiseChip || this.kind === opportunityKinds.SwitchChip
    },

    showsAdvice() {
      return this.kind === opportunityKinds.Advice
    },

    // Slice H's suggestions show inside the rx-market-intelligence rollout, on a row from backend
    // PR 2 on. Anything else is today's cell, chip and all, flag on or off
    usesSuggestions() {
      return this.opportunity.usesSuggestions
    },

    // The campaign's suggestion (§3.9), only for a campaign the trigger picked: never at or above
    // the market, never a lower bid (R13, R20, R25)
    suggestedSetup() {
      return this.opportunity.setup
    },

    // A Static campaign's chip: "$50.05 · 90 s" for a switch of bid and minimum, which the new
    // endpoint writes, and "+$20.05" for a raise that keeps the minimum, today's update-bid
    suggestionChipText() {
      const setup = this.suggestedSetup

      if (this.kind === opportunityKinds.SwitchChip) {
        return `${formatCurrency(setup.bidInUSD)} · ${setup.minimumSeconds} s`
      }

      return this.kind === opportunityKinds.RaiseChip
        ? `+${formatCurrency(setup.bidInUSD - this.currentBidInUSD)}`
        : ''
    },

    // What the chip hands the list's confirmation: the suggestion and the row's Missed count, the
    // one count the confirmation names (R54). The wire's contested counts are not read
    suggestionPayload() {
      const setup = this.suggestedSetup

      return {
        campaign: this.campaign,
        suggestedBidInUSD: setup.bidInUSD,
        minimumSeconds: setup.minimumSeconds,
        changesMinimum: setup.changesMinimum === true,
        minimumBucket: setup.minimumBucket || null,
        missedCalls: this.marketPosition.missedCalls ?? null,
        fromSuggestedSetup: true
      }
    },

    // A RingbaRTB or Programmatic campaign's advice, worded for where its terms come from (I16,
    // I42). Not for a CPA-RTB campaign, whose postback bid is a static amount the budget step's
    // card leaves out as well
    adviceText() {
      return this.opportunity.adviceText
    },

    // The row's Missed count beside its suggestion (R54), in the Missed column's definition and
    // window, so the hover and the column never disagree. Empty at 0 and before the window is known.
    // It replaces R41's contested counts and R45's "chance of winning" sentence: no count but this
    // one reaches a buyer, and nothing says the suggestion would win these auctions back
    missedAuctionsText() {
      if (!this.suggestedSetup) {
        return ''
      }

      return getMissedAuctionsText(this.marketPosition.missedCalls, { short: this.windowShort, long: this.windowLong })
    },

    // The chip's hover: what it does, then the Missed count when there is one (R54). "Raises the bid
    // to $50.05. This campaign lost 7,244 auctions to higher bids from other buyers in the last 24
    // hours." At 0 missed it is the action alone. On a row E1 flagged as losing on conversion the
    // action is followed by the note on why this setup (R62), on the campaign's own terms: "Raises
    // the bid to $64.90. Your conversion rate counts against your bid in these auctions. This campaign
    // lost ..."
    suggestionTitle() {
      const action = getSuggestionActionText(this.suggestedSetup, this.marketPosition?.currentTerms)

      if (!action) {
        return ''
      }

      const notes = this.setupNotes

      return notes ? `${action}. ${notes}` : `${action}.`
    },

    // What the hover says after the setup itself, for the chip and for the dynamic row's advice: the
    // conversion note on a flagged row (R62), then the Missed count (R54), each only when there is one
    setupNotes() {
      return [this.losesOnConversion ? CONVERSION_ADVICE_SENTENCE : '', this.missedAuctionsText]
        .filter(Boolean)
        .join(' ')
    },

    // The dynamic advice's hover: the notes above, none when there are none, so no empty tooltip
    adviceTitle() {
      return this.setupNotes || null
    },

    // The endpoint scored a static row on the campaign's configured bid AT FETCH TIME. An inline
    // edit puts the new bid in the store at once, but the row keeps the old one until the refetch
    // lands, and a chip computed from it could offer to "raise" a $30 bid to a $15 suggestion.
    // Only a static campaign can be told stale this way: a dynamic row is positioned on its
    // observed median, which no config change moves.
    //
    // With Slice H's suggestions on, the minimum counts too: a switch changes it, and the chips were
    // computed on the one the campaign had
    isPositionStale() {
      return this.kind === opportunityKinds.Stale
    },

    stalePositionTooltip() {
      return this.usesSuggestions
        ? 'The bid or minimum changed after this campaign\'s market position was measured. Its suggestion returns once the position refreshes.'
        : 'The bid changed after this campaign\'s market position was measured. Its suggestion returns once the position refreshes.'
    },

    // R59: E1 flagged the row as losing on conversion rather than on price (R62: it may then carry a
    // setup that compensates, which the chip or the advice shows with the note on why)
    losesOnConversion() {
      return this.marketPosition?.losesOnConversion === true
    },

    // R59, R62: the row's conversion advice, inside the rx-market-intelligence rollout like the rest
    // of Slice H (usesSuggestions). The chip and the advice come first in the template, so this is
    // the row E1 flagged and could give no setup, and it never stands beside either
    showsConversionAdvice() {
      return this.kind === opportunityKinds.ConversionAdvice
    },

    conversionAdviceText() {
      return getConversionAdviceText()
    },

    conversionAdviceTooltip() {
      return getConversionAdviceTooltip()
    },

    // R60: a bid E1 positioned but gave no badge, too few delivered calls from other buyers to
    // position it against (R55, R57). Anything this cell could say about the market would rest on
    // the quartiles that row does not have
    isUnpositioned() {
      return this.kind === opportunityKinds.Unpositioned
    },

    // R60: why a positioned row with a badge has no suggestion, whatever the flag says (I8, I40), as
    // the rank band it replaces was: only a market with no suggested setup says so (R66). The chip,
    // the advice, the conversion advice and today's chip all come first in the template, so this
    // reads only rows none of them speaks for
    noSuggestionReason() {
      return this.opportunity.noSuggestionReason
    },

    showsNoSuggestionReason() {
      return this.kind === opportunityKinds.NoSuggestion
    },

    noSuggestionText() {
      return getNoSuggestionText(this.noSuggestionReason)
    },

    // The bid the row was positioned on, so the raise is measured against the same number
    // the suggestion was derived from; falls back to the campaign config only if the
    // endpoint's row is absent
    currentBidInUSD() {
      return this.opportunity.currentBidInUSD
    },

    raiseAmountInUSD() {
      return this.marketPosition.suggestedBidInUSD - this.currentBidInUSD
    },

    // Today's chip, from E1's suggestedBidInUSD: the flag-off list, and a row from a backend older
    // than Slice H's fields. Under Slice H the suggestion comes from suggestedSetup instead
    hasSuggestion() {
      return this.kind === opportunityKinds.LegacyChip
    },

    // The state a Static campaign lands in the moment it acts on a raise: the endpoint still
    // suggests a bid, but the campaign is now at or above it, so there is no chip left and no
    // Leading tier to report. Without this the cell falls to a bare dash and the buyer reads
    // their own successful raise as missing data
    isAtSuggestedBid() {
      return this.kind === opportunityKinds.AtSuggestedBid
    },

    // Names the window only once it is known, for the same reason noBidsText in
    // MarketPositionIndicator does. No call estimates: E1 stopped sending them in backend PR 1
    // (R47). From that PR a row also names the calls its suggestion follows, and their window,
    // which can be the last 7 days on a 24-hour list (§3.7), so it must not say "24h" then: today's
    // ladder on the row's own quartiles until backend PR 2 (I21), the cell's suggested setup, always
    // E2's 7 days, from PR 2 on (§3.9; its rows carry currentTerms)
    atSuggestedBidTooltip() {
      const position = this.marketPosition
      const lead = 'Your bid is at or above the suggested bid.'

      if (typeof position?.positionWindowHours !== 'number') {
        const windowPhrase = this.windowShort ? ` ${this.windowShort}` : ''

        return `${lead} The suggestion only moves as new auctions accumulate over the${windowPhrase} window`
      }

      const suggestionWindow = formatMarketWindow(position.currentTerms !== undefined ? MARKET_CONTEXT_WINDOW_HOURS : position.positionWindowHours)
      const minimum = getMinimumText(position.pricedOn)

      return `${lead} The suggestion follows winning bids${minimum ? ` ${minimum}` : ''} over the ${suggestionWindow.long}, so it only moves as new auctions accumulate.`
    }
  },

  methods: {
    formatCurrency
  }
}
</script>

<style lang="scss" scoped>
// The advice is a sentence, and the column is narrow: let it wrap rather than widen the table
.opportunity-advice {
  max-width: 16rem;
  font-size: 0.875rem;
}

// One line in the table's column. A phone's campaigns list is TouchTable's cards, where "Outbid, no
// raise suggested" ran past a 360px card, so it wraps there
.opportunity-reason {
  white-space: nowrap;

  @include media-breakpoint-down(sm) {
    white-space: normal;
  }
}
</style>
